import { z } from 'zod';

import { ElementCategory } from './element.js';
import { ConfidenceBand } from './requirement.js';

/**
 * THE PRODUCTION PLAN (2026-11-05, plan rev. 4): what a production needs, derived from what the
 * screenplay shows, kept apart from it.
 *
 * Five objects, never shown as one another:
 *   evidence       an exact quote with its scene and span, verified by the server
 *   observation    what a quote directly supports (an event, a beat, a stated age, a state change)
 *   interpretation a classification of an observation, by the model or a rule (hazard, scale,
 *                  "involves a minor character", presence) — it keeps the quote it rests on
 *   requirement    what the production needs: EXPLICIT, DERIVED, SUGGESTED or MANUAL
 *   decision       a person's act, append-only
 *
 * The plan never records a final safety or legal authorisation: it records that a person accepted
 * that a specialist review is needed. Everything here sits behind `PRODUCTION_REASONING`.
 */
export const PRODUCTION_PLAN_CONTRACT_VERSION = '1.0.0';

// ---------------------------------------------------------------------------------------------
// Observations the production reading may return (closed vocabularies)
// ---------------------------------------------------------------------------------------------

/**
 * A meaningful production event, at the granularity a 1st AD plans: one fight is ONE event with
 * beats, not an event per punch. Ordinary movement is not an event.
 */
export const ProductionEventType = z.enum([
  'VEHICLE_ACTION', // a vehicle doing something that needs planning: a swerve, a chase, a crash
  'DRIVING', // performers playing a scene in a moving vehicle
  'FIGHT',
  'FALL',
  'HEIGHT', // action at a height: a ledge, a roof, a climb
  'FIRE',
  'EXPLOSION',
  'DESTRUCTION', // something breaks or is destroyed: a window, a wall, a building
  'GUNFIRE',
  'WATER', // action in or under water
  'WEATHER', // rain, snow, wind, fog the scene states
  'CREATURE', // a creature that is not an ordinary animal
  'TRANSFORMATION', // a change of a body or a face the camera sees
  'SCREEN_CONTENT', // text or images shown on a screen, a sign, a document
  'OFF_SCREEN_SOUND', // something heard, not seen
  'REMOTE_VOICE', // a voice over a radio, a phone, a speaker
  'INTIMACY',
  'CROWD_ACTION', // a group doing something together
  'ANIMAL_ACTION', // an animal seen doing something
  'MACHINERY_ACTION', // a machine in the story working: a crane, a forklift, a conveyor
  'FOOD_DRINK', // eating or drinking on camera
  'PLAYBACK', // music, TV or radio the characters hear
  'DEVICE_IN_USE', // a working practical device: a lamp switched on, a projector running
  'STATE_CHANGE', // a person, costume, object or place changes state
]);
export type ProductionEventType = z.infer<typeof ProductionEventType>;

export const ParticipantRole = z.enum([
  'PERFORMER',
  'GROUP',
  'STORY_OBJECT',
  'VEHICLE',
  'ANIMAL',
  'CREATURE',
  'DEVICE',
  'STRUCTURE',
]);
export type ParticipantRole = z.infer<typeof ParticipantRole>;

/** An INTERPRETATION of an event, never a fact the screenplay states. */
export const Hazard = z.enum(['ORDINARY', 'NOTABLE', 'HAZARDOUS']);
export type Hazard = z.infer<typeof Hazard>;

/** An INTERPRETATION: how much of the frame the event involves. */
export const EventScale = z.enum(['OBJECT', 'PERSON', 'VEHICLE', 'STRUCTURE', 'AREA']);
export type EventScale = z.infer<typeof EventScale>;

/** Where a character is in a scene. Stated when a quote says so (V.O., "over the radio"). */
export const CastPresence = z.enum([
  'ON_SCREEN',
  'OFF_SCREEN_SAME_LOCATION',
  'VOICE_OVER',
  'REMOTE_VOICE',
]);
export type CastPresence = z.infer<typeof CastPresence>;

export const StateKind = z.enum([
  'DAMAGED',
  'DESTROYED',
  'TORN',
  'WET',
  'BLOODIED',
  'DIRTY',
  'BURNED',
  'INJURED',
  'AGED',
  'CHANGED_APPEARANCE',
  'CHANGED_OWNER',
  'REPAIRED',
  'CLEANED',
  'HEALED',
  'SWITCHED_ON',
  'SWITCHED_OFF',
  'OTHER',
]);
export type StateKind = z.infer<typeof StateKind>;

/** States that close an earlier one (a torn shirt mended, a wound healed). */
export const CLOSING_STATES: Readonly<Partial<Record<StateKind, readonly StateKind[]>>> = {
  REPAIRED: ['DAMAGED', 'TORN', 'BURNED'],
  CLEANED: ['DIRTY', 'BLOODIED', 'WET'],
  HEALED: ['INJURED'],
  SWITCHED_OFF: ['SWITCHED_ON'],
};

export const LocationFeatureFacet = z.enum(['VISUAL', 'ARCHITECTURAL', 'ACTION_SPACE', 'CONDITION']);
export type LocationFeatureFacet = z.infer<typeof LocationFeatureFacet>;

// ---------------------------------------------------------------------------------------------
// Plan vocabularies
// ---------------------------------------------------------------------------------------------

export const PlanOrigin = z.enum(['EXPLICIT', 'DERIVED', 'SUGGESTED', 'MANUAL']);
export type PlanOrigin = z.infer<typeof PlanOrigin>;

export const Necessity = z.enum(['REQUIRED', 'CONDITIONAL', 'OPTIONAL']);
export type Necessity = z.infer<typeof Necessity>;

export const ExecutionMethod = z.enum(['PRACTICAL', 'DIGITAL', 'HYBRID', 'OTHER']);
export type ExecutionMethod = z.infer<typeof ExecutionMethod>;

/**
 * Why a CONDITIONAL requirement is not simply required. The `text` says it in words; the code is
 * what the plan evaluates.
 */
export const ConditionCode = z.enum([
  'METHOD_SELECTED', // needed if one of `methods` is chosen for the event
  'PERFORMER_UNDER_LOCAL_AGE', // the performer cast is a minor where the shoot takes place
  'JURISDICTION_RULES', // depends on the rules where the shoot takes place
  'SHOT_OUT_OF_ORDER_OR_REPEATED_TAKES',
  'LIVE_ANIMAL_USED',
  'PUBLIC_LOCATION', // the location chosen is public or needs access control
  'ASSUMPTION_NOT_SET', // a project assumption the plan needs is not set
  'PRODUCER_DECISION',
]);
export type ConditionCode = z.infer<typeof ConditionCode>;

export const Condition = z.object({
  code: ConditionCode,
  methods: z.array(ExecutionMethod).optional(),
  text: z.string().min(1).max(300),
});
export type Condition = z.infer<typeof Condition>;

/** What a production department owns. Not an element category: a vehicle concerns several. */
export const ProductionDepartment = z.enum([
  'ASSISTANT_DIRECTORS',
  'PRODUCTION_OFFICE',
  'CASTING',
  'STUNTS',
  'SPECIAL_EFFECTS',
  'VISUAL_EFFECTS',
  'ART',
  'GRAPHICS_PLAYBACK',
  'CONSTRUCTION',
  'COSTUME',
  'HAIR_MAKEUP',
  'PROSTHETICS',
  'TRANSPORT',
  'ANIMALS',
  'ARMOURY',
  'CAMERA',
  'GRIP_ELECTRIC',
  'SOUND',
  'MUSIC_PLAYBACK',
  'LOCATIONS',
  'HEALTH_SAFETY',
  'SECURITY',
  'INTIMACY_COORDINATION',
  'CHILD_WELFARE',
  'POST_PRODUCTION',
]);
export type ProductionDepartment = z.infer<typeof ProductionDepartment>;

/** What would be sourced. Separate from the category and the department. */
export const ResourceType = z.enum([
  'LOCATION',
  'PERFORMER',
  'SUPPORTING_ARTIST',
  'SPECIALIST_CREW',
  'PHYSICAL_ASSET',
  'VEHICLE',
  'ANIMAL',
  'EQUIPMENT',
  'PRACTICAL_EFFECT',
  'DIGITAL_EFFECT',
  'DIGITAL_ASSET',
  'SET_BUILD',
  'PERMIT_ACCESS',
  'SERVICE',
]);
export type ResourceType = z.infer<typeof ResourceType>;

export const PlanRequirementType = z.enum([
  'ASSET',
  'PERFORMANCE',
  'STUNT_SEQUENCE',
  'EFFECT',
  'SPECIALIST',
  'EQUIPMENT',
  'LOCATION_NEED',
  'PERMIT_ACCESS',
  'SAFETY_REVIEW',
  'COMPLIANCE_REVIEW',
  'CONTINUITY',
  'FACT_REVIEW',
  'SERVICE',
]);
export type PlanRequirementType = z.infer<typeof PlanRequirementType>;

/** The sheet's categories plus LOCATION: where a requirement sits on the breakdown sheet. */
export const PlanCategory = z.union([ElementCategory, z.literal('LOCATION')]);
export type PlanCategory = z.infer<typeof PlanCategory>;

export const RiskFlag = z.enum([
  'STUNT',
  'FIRE',
  'EXPLOSIVE',
  'WEAPON',
  'WATER',
  'HEIGHT',
  'VEHICLE',
  'MINOR',
  'ANIMAL',
  'INTIMACY',
  'CROWD',
  'PUBLIC_ACCESS',
  'HAZARDOUS_MATERIAL',
  'NIGHT_WORK',
  'RIGGING',
]);
export type RiskFlag = z.infer<typeof RiskFlag>;

export const QuantitySource = z.enum(['EXPLICIT', 'DERIVED_MINIMUM', 'ESTIMATE', 'UNKNOWN']);
export type QuantitySource = z.infer<typeof QuantitySource>;

/** A count the engine proposes. A person's count is `approvedQuantity`, never written by the engine. */
export const PlanQuantity = z.object({
  value: z.number().int().positive().nullable(),
  source: QuantitySource,
});
export type PlanQuantity = z.infer<typeof PlanQuantity>;

export const PlanReviewStatus = z.enum(['PENDING', 'ACCEPTED', 'REJECTED', 'NEEDS_REVALIDATION']);
export type PlanReviewStatus = z.infer<typeof PlanReviewStatus>;

/** Whether the row's basis still holds. MANUAL rows have no rule basis. */
export const BasisStatus = z.enum(['VALID', 'CHANGED', 'NOT_REPRODUCED', 'MANUAL']);
export type BasisStatus = z.infer<typeof BasisStatus>;

export const EventMatchStatus = z.enum(['NEW', 'MATCHED', 'AMBIGUOUS']);
export type EventMatchStatus = z.infer<typeof EventMatchStatus>;

export const OptionStatus = z.enum(['PROPOSED', 'SELECTED', 'REJECTED']);
export type OptionStatus = z.infer<typeof OptionStatus>;

export const Applicability = z.enum([
  'REQUIRED_IF_SELECTED',
  'POTENTIALLY_REQUIRED',
  'OPTIONAL',
  'NOT_APPLICABLE',
]);
export type Applicability = z.infer<typeof Applicability>;

/** A requirement's applicability once the chosen methods are known. Computed on read. */
export const EffectiveApplicability = z.enum([
  'APPLIES', // required, or required by the selected method
  'PENDING_METHOD', // depends on a method nobody has selected yet
  'POSSIBLE', // potentially required or optional under the selected method
  'NOT_APPLICABLE', // the selected method does not need it
]);
export type EffectiveApplicability = z.infer<typeof EffectiveApplicability>;

/** A catalogue match is a CANDIDATE, never proof that it is available. */
export const SourcingStatus = z.enum([
  'NOT_SEARCHED',
  'MATCH_CANDIDATES_FOUND',
  'NO_MATCH',
  'EXTERNAL_SOURCING',
  'CONFIRMED',
]);
export type SourcingStatus = z.infer<typeof SourcingStatus>;

/** Whether ScoutAround can search for this kind of resource at all. */
export const CatalogueCapability = z.enum(['SUPPORTED', 'NOT_SUPPORTED']);
export type CatalogueCapability = z.infer<typeof CatalogueCapability>;

export const SceneReadingStatus = z.enum(['READ', 'READ_EMPTY', 'UNREAD', 'FAILED']);
export type SceneReadingStatus = z.infer<typeof SceneReadingStatus>;

export const ProductionAnalysisStatus = z.enum(['COMPLETED', 'PARTIAL', 'FAILED', 'UNAVAILABLE']);
export type ProductionAnalysisStatus = z.infer<typeof ProductionAnalysisStatus>;

export const ContinuityRole = z.enum(['ESTABLISHED', 'AFFECTED', 'CLOSED']);
export type ContinuityRole = z.infer<typeof ContinuityRole>;

export const SceneApplicability = z.enum(['APPLIES', 'POSSIBLE']);
export type SceneApplicability = z.infer<typeof SceneApplicability>;

export const VersionComparison = z.enum(['NEW', 'UNCHANGED', 'CHANGED', 'REMOVED', 'UNCERTAIN_MATCH']);
export type VersionComparison = z.infer<typeof VersionComparison>;

export const InterpretationSource = z.enum(['MODEL', 'RULE']);
export type InterpretationSource = z.infer<typeof InterpretationSource>;

// ---------------------------------------------------------------------------------------------
// Assumptions
// ---------------------------------------------------------------------------------------------

/**
 * A project assumption is read from an existing project field and NEVER defaulted: a missing
 * value is NOT_SET. Scene and event overrides take precedence (event, then scene, then project).
 * SEQUENCE is a reserved scope: the workspace has no sequence entity yet.
 */
export const AssumptionKey = z.enum([
  'TERRITORY',
  'PRODUCTION_MODEL',
  'FORMAT',
  'BUDGET_BAND',
  'STUDIO_PREFERENCE',
  'INTERIOR_EXTERIOR',
  'SHOOT_WINDOW',
  'CONSTRAINTS_NOTE',
  'EXECUTION_PREFERENCE',
]);
export type AssumptionKey = z.infer<typeof AssumptionKey>;

/** Keys a person may override for one scene or one event. */
export const OVERRIDABLE_ASSUMPTIONS: readonly AssumptionKey[] = [
  'EXECUTION_PREFERENCE',
  'STUDIO_PREFERENCE',
  'TERRITORY',
];

export const OverrideScope = z.enum(['SCENE', 'EVENT', 'SEQUENCE']);
export type OverrideScope = z.infer<typeof OverrideScope>;

export const AssumptionSource = z.enum(['EVENT_OVERRIDE', 'SCENE_OVERRIDE', 'PROJECT_FIELD', 'NOT_SET']);
export type AssumptionSource = z.infer<typeof AssumptionSource>;

export const AssumptionView = z.object({
  key: AssumptionKey,
  value: z.string().nullable(),
  source: AssumptionSource,
  /** The project field it was read from, when it was. */
  field: z.string().nullable(),
});
export type AssumptionView = z.infer<typeof AssumptionView>;

// ---------------------------------------------------------------------------------------------
// Resource specifications: a common interface plus an extension per resource type
// ---------------------------------------------------------------------------------------------

const DateRange = z.object({ start: z.string().nullable(), end: z.string().nullable() });
const KeyValue = z.object({ key: z.string().min(1).max(80), value: z.string().max(300) });

/** What every resource request carries. An unknown value is null, never guessed. */
const resourceSpecBase = {
  quantity: z.number().int().positive().nullable(),
  unit: z.string().max(40).nullable(),
  dates: DateRange.nullable(),
  territory: z.array(z.string().max(80)).nullable(),
  constraints: z.array(z.string().max(300)).max(20),
  requiredCapabilities: z.array(z.string().max(200)).max(20),
};

export const LocationResourceSpec = z.object({
  type: z.literal('LOCATION'),
  ...resourceSpecBase,
  geography: z.string().max(200).nullable(),
  intExt: z.enum(['INT', 'EXT', 'INT_EXT', 'UNKNOWN']),
  access: z.array(z.string().max(200)).max(20),
  physicalFeatures: z.array(z.string().max(200)).max(40),
  restrictions: z.array(z.string().max(200)).max(20),
  availabilityWindow: DateRange.nullable(),
});

export const EquipmentResourceSpec = z.object({
  type: z.literal('EQUIPMENT'),
  ...resourceSpecBase,
  equipmentClass: z.string().max(120).nullable(),
  technicalSpecs: z.array(KeyValue).max(30),
  power: z.string().max(120).nullable(),
  operatorRequired: z.boolean().nullable(),
});

export const DigitalAssetResourceSpec = z.object({
  type: z.literal('DIGITAL_ASSET'),
  ...resourceSpecBase,
  assetKind: z.string().max(120).nullable(),
  resolution: z.string().max(80).nullable(),
  formats: z.array(z.string().max(40)).max(20),
  usageRights: z.string().max(300).nullable(),
  reconstructionQuality: z.string().max(120).nullable(),
  virtualProductionCompatible: z.boolean().nullable(),
});

export const SpecialistResourceSpec = z.object({
  type: z.literal('SPECIALIST_CREW'),
  ...resourceSpecBase,
  role: z.string().min(1).max(120),
  /** Qualifications a person must confirm; the plan never asserts one is held or required by law. */
  qualificationsToConfirm: z.array(z.string().max(200)).max(20),
  engagementScope: z.string().max(200).nullable(),
});

const GENERIC_RESOURCE_TYPES = [
  'PERFORMER',
  'SUPPORTING_ARTIST',
  'PHYSICAL_ASSET',
  'VEHICLE',
  'ANIMAL',
  'PRACTICAL_EFFECT',
  'DIGITAL_EFFECT',
  'SET_BUILD',
  'PERMIT_ACCESS',
  'SERVICE',
] as const satisfies readonly ResourceType[];

export const GenericResourceSpec = z.object({
  type: z.enum(GENERIC_RESOURCE_TYPES),
  ...resourceSpecBase,
  specs: z.array(KeyValue).max(30),
});

export const ResourceSpec = z.discriminatedUnion('type', [
  LocationResourceSpec,
  EquipmentResourceSpec,
  DigitalAssetResourceSpec,
  SpecialistResourceSpec,
  GenericResourceSpec,
]);
export type ResourceSpec = z.infer<typeof ResourceSpec>;

/**
 * The extensibility contract: one schema per resource type, versioned. Adding a type-specific
 * extension is adding an entry here; a stored spec names the version it was written against.
 */
export const RESOURCE_SPEC_SCHEMAS: Readonly<Record<ResourceType, { version: string; schema: z.ZodType }>> = {
  LOCATION: { version: '1.0.0', schema: LocationResourceSpec },
  EQUIPMENT: { version: '1.0.0', schema: EquipmentResourceSpec },
  DIGITAL_ASSET: { version: '1.0.0', schema: DigitalAssetResourceSpec },
  SPECIALIST_CREW: { version: '1.0.0', schema: SpecialistResourceSpec },
  PERFORMER: { version: '1.0.0', schema: GenericResourceSpec },
  SUPPORTING_ARTIST: { version: '1.0.0', schema: GenericResourceSpec },
  PHYSICAL_ASSET: { version: '1.0.0', schema: GenericResourceSpec },
  VEHICLE: { version: '1.0.0', schema: GenericResourceSpec },
  ANIMAL: { version: '1.0.0', schema: GenericResourceSpec },
  PRACTICAL_EFFECT: { version: '1.0.0', schema: GenericResourceSpec },
  DIGITAL_EFFECT: { version: '1.0.0', schema: GenericResourceSpec },
  SET_BUILD: { version: '1.0.0', schema: GenericResourceSpec },
  PERMIT_ACCESS: { version: '1.0.0', schema: GenericResourceSpec },
  SERVICE: { version: '1.0.0', schema: GenericResourceSpec },
};

/** The one resource type ScoutAround can search today: its location catalogue. */
export const CATALOGUE_SUPPORTED_RESOURCES: readonly ResourceType[] = ['LOCATION'];

// ---------------------------------------------------------------------------------------------
// Views (API answers)
// ---------------------------------------------------------------------------------------------

export const EvidenceStatus = z.enum(['VERIFIED', 'RELOCATED', 'AMBIGUOUS_MATCH']);
export type EvidenceStatus = z.infer<typeof EvidenceStatus>;

export const EvidenceSpan = z.object({
  sceneId: z.string(),
  blockId: z.string().nullable(),
  start: z.number().int().nullable(),
  end: z.number().int().nullable(),
  text: z.string(),
  status: EvidenceStatus,
});
export type EvidenceSpan = z.infer<typeof EvidenceSpan>;

export const InterpretationKey = z.enum([
  'HAZARD',
  'SCALE',
  'INVOLVES_MINOR_CHARACTER',
  'HEARD_ONLY',
  'PRESENCE',
]);
export type InterpretationKey = z.infer<typeof InterpretationKey>;

/** A classification, labelled as one; it never reads as a screenplay fact. */
export const InterpretationView = z.object({
  key: InterpretationKey,
  value: z.string(),
  source: InterpretationSource,
  basis: EvidenceSpan.nullable(),
});
export type InterpretationView = z.infer<typeof InterpretationView>;

export const ParticipantLink = z.enum(['LINKED', 'AMBIGUOUS', 'STORY_ENTITY']);
export type ParticipantLink = z.infer<typeof ParticipantLink>;

export const ParticipantView = z.object({
  name: z.string(),
  role: ParticipantRole,
  canonicalKey: z.string().nullable(),
  mentionIds: z.array(z.string()),
  link: ParticipantLink,
});
export type ParticipantView = z.infer<typeof ParticipantView>;

export const ProductionEventView = z.object({
  id: z.string(),
  lineageId: z.string(),
  matchStatus: EventMatchStatus,
  candidateLineageIds: z.array(z.string()),
  sceneId: z.string(),
  orderInType: z.number().int(),
  type: ProductionEventType,
  title: z.string(),
  beats: z.array(z.object({ action: z.string(), evidence: EvidenceSpan.nullable() })),
  participants: z.array(ParticipantView),
  /** Observations a quote states: a stated age, a state change. */
  observations: z.object({
    statedAge: z.object({ years: z.number().int(), evidence: EvidenceSpan }).nullable(),
    state: z
      .object({ subject: z.string(), subjectKey: z.string().nullable(), state: StateKind })
      .nullable(),
  }),
  interpretations: z.array(InterpretationView),
  evidence: EvidenceSpan,
  band: ConfidenceBand,
  reviewStatus: PlanReviewStatus,
  basisStatus: BasisStatus,
  rowVersion: z.number().int(),
});
export type ProductionEventView = z.infer<typeof ProductionEventView>;

export const DepartmentTask = z.object({
  department: ProductionDepartment,
  task: z.string().max(200),
  primary: z.boolean(),
});
export type DepartmentTask = z.infer<typeof DepartmentTask>;

/** One scene's part in a requirement: aggregated rows keep each scene's own need and evidence. */
export const SceneEntry = z.object({
  sceneId: z.string(),
  need: z.string().max(300),
  evidence: z.array(EvidenceSpan),
  applicability: SceneApplicability,
  continuityRole: ContinuityRole.nullable(),
});
export type SceneEntry = z.infer<typeof SceneEntry>;

export const AnchorScope = z.enum(['SCREENPLAY', 'LOCATION', 'ENTITY', 'EVENT', 'SCENE']);
export type AnchorScope = z.infer<typeof AnchorScope>;

/**
 * What a requirement's logical identity is made of. `outputId` names the MEANING (a stunt
 * coordinator, one location's access), never a rule version or a row id; several rules may feed
 * one output. Only an EVENT-scoped anchor names lineages, only a SCENE-scoped one a scene.
 */
export const RequirementAnchor = z.object({
  outputId: z.string(),
  scope: AnchorScope,
  discriminator: z.string(),
  entities: z.array(z.string()),
  eventLineageIds: z.array(z.string()),
  sceneId: z.string().nullable(),
});
export type RequirementAnchor = z.infer<typeof RequirementAnchor>;

export const DerivedFrom = z.object({
  mentionIds: z.array(z.string()),
  eventLineageIds: z.array(z.string()),
  sceneIds: z.array(z.string()),
  assumptionKeys: z.array(AssumptionKey),
  /** Absent on a MANUAL row. */
  anchor: RequirementAnchor.nullable(),
});
export type DerivedFrom = z.infer<typeof DerivedFrom>;

export const RegulatoryCheck = z.object({
  /** Always conditional: the plan states that rules may apply, never which or that they are met. */
  applies: z.literal('DEPENDS_ON_JURISDICTION'),
  territory: z.array(z.string()).nullable(),
  note: z.string().max(300),
});
export type RegulatoryCheck = z.infer<typeof RegulatoryCheck>;

export const SourcingView = z.object({
  status: SourcingStatus,
  capability: CatalogueCapability,
  confirmedByUserId: z.string().nullable(),
  confirmedAt: z.string().nullable(),
  reference: z.string().nullable(),
  note: z.string().nullable(),
  /** The location requirement it is matched through, when it is one. */
  locationRequirementId: z.string().nullable(),
  candidateCount: z.number().int().nullable(),
});
export type SourcingView = z.infer<typeof SourcingView>;

/** The fields a person reviews; the reviewed snapshot holds these as they were decided. */
export const RequirementContent = z.object({
  name: z.string(),
  description: z.string(),
  requirementType: PlanRequirementType,
  primaryCategory: PlanCategory.nullable(),
  departments: z.array(DepartmentTask),
  resourceType: ResourceType.nullable(),
  resourceSpec: ResourceSpec.nullable(),
  necessity: Necessity,
  condition: Condition.nullable(),
  scenes: z.array(SceneEntry),
  quantity: PlanQuantity,
  riskFlags: z.array(RiskFlag),
  specialistReview: z.boolean(),
});
export type RequirementContent = z.infer<typeof RequirementContent>;

export const FieldDiff = z.object({
  field: z.string(),
  reviewed: z.unknown(),
  current: z.unknown(),
});
export type FieldDiff = z.infer<typeof FieldDiff>;

export const ProductionRequirementView = RequirementContent.extend({
  id: z.string(),
  requirementKey: z.string(),
  origin: PlanOrigin,
  eventLineageIds: z.array(z.string()),
  derivedFrom: DerivedFrom,
  /** `RULE_ID@version` of every rule that produced it. */
  rules: z.array(z.string()),
  /** Why the plan proposes it, in words, from the rules and inputs. */
  explanation: z.string(),
  band: ConfidenceBand,
  regulatoryCheck: RegulatoryCheck.nullable(),
  approvedQuantity: z.number().int().positive().nullable(),
  reviewStatus: PlanReviewStatus,
  basisStatus: BasisStatus,
  reviewed: z
    .object({
      decision: z.enum(['ACCEPTED', 'REJECTED']),
      snapshot: RequirementContent,
      byUserId: z.string().nullable(),
      at: z.string(),
    })
    .nullable(),
  /** Reviewed vs current, field by field; empty when nothing material changed. */
  diff: z.array(FieldDiff),
  /** Accepted, basis VALID and its condition satisfied. */
  active: z.boolean(),
  effectiveApplicability: EffectiveApplicability,
  sourcing: SourcingView.nullable(),
  provenance: z.object({
    model: z.string().nullable(),
    promptVersion: z.string().nullable(),
    aiCallLogId: z.string().nullable(),
  }),
  comparison: VersionComparison.nullable(),
  /** What a person decided on this requirement in the previous script version; never carried over. */
  previous: z
    .object({ decision: z.enum(['ACCEPTED', 'REJECTED']).nullable(), approvedQuantity: z.number().int().nullable() })
    .nullable(),
  rowVersion: z.number().int(),
});
export type ProductionRequirementView = z.infer<typeof ProductionRequirementView>;

export const OptionDependency = z.object({
  requirementKey: z.string(),
  requirementId: z.string().nullable(),
  applicability: Applicability,
});
export type OptionDependency = z.infer<typeof OptionDependency>;

export const ExecutionOptionView = z.object({
  id: z.string(),
  optionKey: z.string(),
  eventLineageId: z.string(),
  method: ExecutionMethod,
  ruleId: z.string(),
  description: z.string(),
  advantages: z.array(z.string()),
  constraints: z.array(z.string()),
  risks: z.array(z.string()),
  assumptions: z.array(z.string()),
  dependencies: z.array(OptionDependency),
  /** The project's (or an override's) execution preference matches. A mark, never a filter. */
  matchesPreference: z.boolean(),
  status: OptionStatus,
  needsRevalidation: z.boolean(),
  rowVersion: z.number().int(),
});
export type ExecutionOptionView = z.infer<typeof ExecutionOptionView>;

export const SceneReadingView = z.object({
  sceneId: z.string(),
  status: SceneReadingStatus,
  presence: z.array(
    z.object({
      name: z.string(),
      presence: CastPresence,
      /** True when a quote states it (V.O., "over the radio"): then it is an observation. */
      stated: z.boolean(),
      evidence: EvidenceSpan.nullable(),
    }),
  ),
  locationFeatures: z.array(
    z.object({ facet: LocationFeatureFacet, feature: z.string(), evidence: EvidenceSpan }),
  ),
});
export type SceneReadingView = z.infer<typeof SceneReadingView>;

export const ProductionAnalysisView = z.object({
  status: ProductionAnalysisStatus,
  rulesFingerprint: z.string(),
  derivedAt: z.string().nullable(),
  scenesRead: z.number().int(),
  scenesUnread: z.number().int(),
  comparedScriptVersionId: z.string().nullable(),
});
export type ProductionAnalysisView = z.infer<typeof ProductionAnalysisView>;

export const AssumptionOverrideView = z.object({
  id: z.string(),
  scope: OverrideScope,
  scopeId: z.string(),
  key: AssumptionKey,
  value: z.string(),
});
export type AssumptionOverrideView = z.infer<typeof AssumptionOverrideView>;

export const CoverageMark = z.enum(['EXTRACTED', 'READ', 'DERIVED', 'SUGGESTED', 'NOT_SUPPORTED']);
export type CoverageMark = z.infer<typeof CoverageMark>;

export const CoverageRow = z.object({
  department: ProductionDepartment,
  marks: z.array(CoverageMark),
  covered: z.array(z.string()),
  notSupported: z.array(z.string()),
});
export type CoverageRow = z.infer<typeof CoverageRow>;

export const RemovedRequirementView = z.object({
  requirementKey: z.string(),
  name: z.string(),
  wasReviewed: z.enum(['ACCEPTED', 'REJECTED']).nullable(),
});
export type RemovedRequirementView = z.infer<typeof RemovedRequirementView>;

export const ProductionPlanView = z.object({
  scriptVersionId: z.string(),
  contractVersion: z.string(),
  analysis: ProductionAnalysisView.nullable(),
  assumptions: z.array(AssumptionView),
  overrides: z.array(AssumptionOverrideView),
  readings: z.array(SceneReadingView),
  events: z.array(ProductionEventView),
  requirements: z.array(ProductionRequirementView),
  options: z.array(ExecutionOptionView),
  /** Requirements of the previous version with no counterpart in this one. */
  removedSincePrevious: z.array(RemovedRequirementView),
  coverage: z.array(CoverageRow),
});
export type ProductionPlanView = z.infer<typeof ProductionPlanView>;

// ---------------------------------------------------------------------------------------------
// Request bodies
// ---------------------------------------------------------------------------------------------

export const RequirementDecisionAction = z.enum(['ACCEPT', 'REJECT', 'RESTORE', 'EDIT', 'REVALIDATE']);
export type RequirementDecisionAction = z.infer<typeof RequirementDecisionAction>;

export const RequirementEdit = z.object({
  name: z.string().trim().min(1).max(255).optional(),
  primaryCategory: PlanCategory.nullable().optional(),
  departments: z.array(DepartmentTask).max(12).optional(),
  approvedQuantity: z.number().int().min(1).max(9999).nullable().optional(),
});
export type RequirementEdit = z.infer<typeof RequirementEdit>;

export const DecideRequirementBody = z.object({
  action: RequirementDecisionAction,
  rowVersion: z.number().int(),
  /** REVALIDATE only: ACCEPT or REJECT the current derivation. */
  verdict: z.enum(['ACCEPT', 'REJECT']).optional(),
  /** REVALIDATE + ACCEPT only: keep the approved quantity instead of clearing it. */
  keepApprovedQuantity: z.boolean().optional(),
  edit: RequirementEdit.optional(),
  note: z.string().max(500).optional(),
});
export type DecideRequirementBody = z.infer<typeof DecideRequirementBody>;

export const AddRequirementBody = z.object({
  scriptVersionId: z.string(),
  name: z.string().trim().min(1).max(255),
  description: z.string().max(1000).default(''),
  requirementType: PlanRequirementType,
  primaryCategory: PlanCategory.nullable(),
  departments: z.array(DepartmentTask).max(12).default([]),
  resourceType: ResourceType.nullable().default(null),
  sceneIds: z.array(z.string()).max(500).default([]),
  approvedQuantity: z.number().int().min(1).max(9999).nullable().default(null),
});
export type AddRequirementBody = z.infer<typeof AddRequirementBody>;

export const OptionDecisionBody = z.object({
  action: z.enum(['SELECT', 'UNSELECT', 'REJECT', 'RESTORE', 'REVALIDATE']),
  rowVersion: z.number().int(),
});
export type OptionDecisionBody = z.infer<typeof OptionDecisionBody>;

export const EventDecisionBody = z.object({
  action: z.enum(['REJECT', 'RESTORE', 'CONFIRM_SAME', 'CONFIRM_DIFFERENT', 'REVALIDATE']),
  rowVersion: z.number().int(),
  /** CONFIRM_SAME only: the candidate lineage this event continues. */
  lineageId: z.string().optional(),
});
export type EventDecisionBody = z.infer<typeof EventDecisionBody>;

export const SetOverrideBody = z.object({
  scriptVersionId: z.string(),
  scope: z.enum(['SCENE', 'EVENT']),
  scopeId: z.string(),
  key: AssumptionKey,
  /** null removes the override. */
  value: z.string().max(120).nullable(),
});
export type SetOverrideBody = z.infer<typeof SetOverrideBody>;

export const SourcingDecisionBody = z
  .object({
    status: z.enum(['NOT_SEARCHED', 'EXTERNAL_SOURCING', 'CONFIRMED']),
    rowVersion: z.number().int(),
    reference: z.string().trim().max(300).optional(),
    note: z.string().trim().max(500).optional(),
  })
  .refine((b) => b.status !== 'CONFIRMED' || (b.reference ?? b.note ?? '').length > 0, {
    message: 'A confirmed resource needs a reference or a note saying how it was confirmed.',
    path: ['reference'],
  });
export type SourcingDecisionBody = z.infer<typeof SourcingDecisionBody>;
